// The player: one transport for video and audio — scrub, ten-second skips, elapsed over total,
// speed and volume — over a real media element streaming the file's decrypted copy. Audio swaps the
// picture for a waveform that doubles as the scrub bar. Video adds full screen, and only video,
// since there is nothing to enlarge on a sound file.
//
// The element is the one source of truth: controls only ever tell it what to do, and what is drawn
// is whatever its own events last reported, so the transport cannot drift from what is playing.
//
// Full screen asks the browser for the real thing. A page that is refused it fills the preview
// instead and says so, rather than leaving a button that appears to do nothing; filling is held by
// the preview, so Escape can bring the player back before it closes anything.
//
// Expanded, by full screen or filling, the transport lies over the picture rather than below it,
// and slides away once the pointer has rested a moment on a playing video, so it doesn't sit over
// the picture. Moving the pointer, or a key, brings it back. It stays while paused, while the
// pointer is over it, and while the keyboard is working in it.
//
// A video can be zoomed into and dragged around (see "useZoom"), from the transport or with the
// plus, minus and zero keys, in the preview or full screen. Double clicking is left alone, since a
// click already plays and pauses.
//
// The keyboard works throughout: space or K to play, the arrows to skip ten seconds, M to mute and
// F for full screen — never while a field has the caret.
//
// The copy streamed from can be lost mid-play, when its url runs out. An element that fails after
// it had the copy's header says so through "onLost", and picks up where it was, playing or not, from
// the source it is handed next. One that fails before then never had the file to begin with.
// Whether it is playing is passed on through "onPlayingChange", so a copy no one is playing can be let
// go.
//
// Waiting is shown, never left blank. Until the element has read the copy's header, while it waits
// for data it was asked to play, and while it seeks to somewhere it has not fetched, a ring turns
// where the play button will be — over the picture for video, in the artwork's place for audio — so
// a player filling its buffer never reads as a frozen one. The ring carries no percentage, since the
// element has no honest one to give; what it can say truthfully is how much is ready, so the scrub
// bar, and the waveform for audio, show the fetched stretch around the playhead. The preview turns
// the same ring while the copy is still being decrypted, so the two waits read as one.
//
// A browser only reads a little way past the playhead, however fast the connection, which leaves a
// listener on a patchy one stalling every time it drops. Where it can, the element is pointed at the
// media relay instead (see "mediaRelayTools"), which fetches the whole copy once, as fast as the
// connection allows, and plays it as it arrives. The bars then also show how much of it has come,
// taken as the same share of the running time as of the bytes — close enough to see the copy filling
// in, though not exact for media whose bitrate varies.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useEffect, useRef, useState} from 'react'

import AudioIcon from '@mui/icons-material/AudiotrackRounded'
import BackIcon from '@mui/icons-material/Replay10Rounded'
import CollapseIcon from '@mui/icons-material/FullscreenExitRounded'
import ExpandIcon from '@mui/icons-material/FullscreenRounded'
import ForwardIcon from '@mui/icons-material/Forward10Rounded'
import IconButton from '@mui/material/IconButton'
import MuteIcon from '@mui/icons-material/VolumeOffRounded'
import PauseIcon from '@mui/icons-material/PauseRounded'
import PlayIcon from '@mui/icons-material/PlayArrowRounded'
import VolumeIcon from '@mui/icons-material/VolumeUpRounded'
import ZoomInIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutIcon from '@mui/icons-material/ZoomOutRounded'

import {formatBytes} from '../../lib/fileTools'
import {openMediaRelay} from '../../lib/mediaRelayTools'
import useZoom, {formatZoom} from '../../lib/hooks/useZoom'

import './mediaPlayer.scss'

const SKIP_SECONDS = 10
const RATES = [1, 1.25, 1.5, 2, 0.75]
const WAVE_BARS = 84
const VIDEO_ZOOM = 4
// How long the pointer rests before an expanded video's transport slides away.
const TRANSPORT_IDLE = 2500

const twoDigits = value => String(value).padStart(2, '0')

export const formatClock = totalSeconds => {
  const seconds = Math.floor(totalSeconds || 0)
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  return hours ?
    `${hours}:${twoDigits(minutes)}:${twoDigits(seconds % 60)}` :
    `${minutes}:${twoDigits(seconds % 60)}`
}

// The quietest a bar is drawn, so silence still leaves something to scrub over.
const LOWEST_BAR = 6

// A recording's real waveform is measured by the worker as it secures the file. One secured before
// that is opened with a shape drawn from its name instead, since measuring it here would mean
// decoding the whole recording before it could start to play. Seeded, so a file always wears the
// same one, until it has been opened once and its real one is measured.
const seededHeights = name => {
  let seed = [...name].reduce((sum, character) => (sum * 31 + character.charCodeAt(0)) % 999983, 0)

  return Array.from({length: WAVE_BARS}, (bar, index) => {
    seed = (seed * 1103515 + 12345) % 2147483

    const envelope = 0.34 + 0.66 * Math.abs(Math.sin(index / 6.5) * Math.cos(index / 23))

    return Math.round(Math.max(LOWEST_BAR, Math.min(100, envelope * (46 + (seed % 54)))))
  })
}

export const hasWaveform = file => file.thumbnail?.peaks?.length === WAVE_BARS

export const waveHeights = file => (
  hasWaveform(file) ? file.thumbnail.peaks.map(peak => Math.max(LOWEST_BAR, peak)) : seededHeights(file.filename)
)

const pointerRatio = event => {
  const {left, width} = event.currentTarget.getBoundingClientRect()

  return width ? Math.max(0, Math.min(1, (event.clientX - left) / width)) : 0
}

// A track that follows the pointer for as long as it is pressed, so it can be dragged as well as
// clicked.
const trackHandlers = onRatio => ({
  onPointerDown: event => {
    event.currentTarget.setPointerCapture?.(event.pointerId)
    onRatio(pointerRatio(event))
  },
  onPointerMove: event => event.buttons && onRatio(pointerRatio(event))
})

// The picture is letterboxed inside the element, which fills the surface. Until the element has
// read the video's size, all of it is taken for the picture.
const pictureBox = (element, surface) => {
  const width = surface.offsetWidth
  const height = surface.offsetHeight
  const {videoWidth, videoHeight} = element

  if (!videoWidth || !videoHeight) return {left: 0, top: 0, width, height}

  const fit = Math.min(width / videoWidth, height / videoHeight)

  return {left: (width - videoWidth * fit) / 2, top: (height - videoHeight * fit) / 2, width: videoWidth * fit, height: videoHeight * fit}
}

export const isTyping = target => ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)

const bufferedRanges = element => {
  const {buffered} = element

  return Array.from({length: buffered?.length ?? 0}, (range, index) => ({from: buffered.start(index), to: buffered.end(index)}))
}

// The fetched stretch the playhead sits in, which is what can play without waiting.
const bufferedAround = element => {
  const at = element.currentTime

  return bufferedRanges(element).find(({from, to}) => from <= at && at <= to) || {from: at, to: at}
}

const isBuffered = element => {
  const {from, to} = bufferedAround(element)

  return to > from
}

export const WaitRing = ({Icon = PlayIcon}) => (
  <span className='wait-ring'>
    <svg className='wait-arc' viewBox='0 0 74 74' aria-hidden='true'>
      <circle className='wait-track' cx='37' cy='37' r='35' />
      <circle className='wait-fill' cx='37' cy='37' r='35' pathLength='100' strokeDasharray='26 74' />
    </svg>
    <Icon className='wait-glyph' />
  </span>
)

WaitRing.propTypes = {Icon: PropTypes.elementType}

export const PlayerWait = ({label, detail, Icon}) => (
  <div className='player-wait' role='status'>
    <WaitRing Icon={Icon} />
    <span className='wait-label'>{label}</span>
    {detail && <span className='wait-detail'>{detail}</span>}
  </div>
)

PlayerWait.propTypes = {label: PropTypes.string.isRequired, detail: PropTypes.string, Icon: PropTypes.elementType}

const STARTS_ON_ITS_OWN = 'Playback starts on its own'

const MediaPlayer = ({file, src, video, filling, onFill, onNotice, onLost, onPlayingChange}) => {
  const media = useRef(null)
  const frame = useRef(null)
  const loaded = useRef(false)
  const resume = useRef(null)
  const idleTimer = useRef(null)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [sound, setSound] = useState({volume: 1, muted: false})
  const [rate, setRate] = useState(1)
  const [fullScreen, setFullScreen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [stalled, setStalled] = useState(false)
  const [buffered, setBuffered] = useState({from: 0, to: 0})
  const [idle, setIdle] = useState(false)
  const [overTransport, setOverTransport] = useState(false)
  const [relay] = useState(openMediaRelay)
  const [received, setReceived] = useState({bytes: 0, of: file.filesize || 0})
  const zoom = useZoom({most: VIDEO_ZOOM, contentBox: surface => pictureBox(media.current, surface), doubleClick: false})

  const played = duration ? Math.min(100, (position / duration) * 100) : 0
  const waiting = loading || stalled
  const percentOf = seconds => (duration ? Math.min(100, (seconds / duration) * 100) : 0)
  const arrived = received.of ? (received.bytes / received.of) * duration : 0
  const ready = arrived && position <= arrived ? {from: 0, to: Math.max(arrived, buffered.to)} : buffered
  const bufferedFrom = percentOf(ready.from)
  const bufferedTo = percentOf(ready.to)
  const followBuffer = event => setBuffered(bufferedAround(event.currentTarget))
  const PlayGlyph = playing ? PauseIcon : PlayIcon
  const expanded = fullScreen || filling
  const tucked = expanded && playing && idle && !overTransport
  const level = sound.muted ? 0 : sound.volume

  const wake = () => {
    setIdle(false)
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => setIdle(true), TRANSPORT_IDLE)
  }

  const toggle = () => (media.current.paused ? media.current.play().catch(() => {}) : media.current.pause())
  const seek = seconds => { media.current.currentTime = Math.max(0, Math.min(duration || 0, seconds)) }
  const seekTo = ratio => seek(ratio * duration)
  const skip = seconds => seek(media.current.currentTime + seconds)
  const toggleMute = () => { media.current.muted = !media.current.muted }
  const nextRate = () => { media.current.playbackRate = RATES[(RATES.indexOf(rate) + 1) % RATES.length] }

  const setLevel = ratio => {
    media.current.volume = ratio
    media.current.muted = ratio === 0
  }

  const toggleFullScreen = () => {
    if (document.fullscreenElement) return document.exitFullscreen().catch(() => {})
    if (filling) return onFill(false)

    const fallBack = () => {
      onFill(true)
      onNotice('Full screen is blocked here — filling the frame instead')
    }

    if (!frame.current.requestFullscreen) return fallBack()

    return frame.current.requestFullscreen().catch(fallBack)
  }

  const begin = () => {
    setLoading(true)
    setStalled(false)
    setBuffered({from: 0, to: 0})
  }

  const pickUp = () => {
    setLoading(false)
    loaded.current = true

    if (!resume.current) return

    const {at, wasPlaying, speed} = resume.current

    resume.current = null
    // A new source starts the element back at normal speed.
    media.current.playbackRate = speed
    media.current.currentTime = at
    if (wasPlaying) media.current.play().catch(() => {})
  }

  const report = isPlaying => {
    setPlaying(isPlaying)
    onPlayingChange(isPlaying)
  }

  const lose = () => {
    if (!loaded.current) return

    loaded.current = false
    resume.current = {at: media.current.currentTime, wasPlaying: playing, speed: media.current.playbackRate}
    onLost()
  }

  const shortcuts = [
    [[' ', 'k'], toggle],
    [['arrowleft'], () => skip(-SKIP_SECONDS)],
    [['arrowright'], () => skip(SKIP_SECONDS)],
    [['m'], toggleMute],
    [video ? ['f'] : [], toggleFullScreen],
    [video ? ['+', '='] : [], zoom.zoomIn],
    [video ? ['-'] : [], zoom.zoomOut],
    [video ? ['0'] : [], zoom.reset]
  ]

  // Re-bound every render so a key always reaches the controls as they now stand.
  useEffect(() => {
    const handleKey = event => {
      const [, shortcut] = shortcuts.find(([keys]) => keys.includes(event.key.toLowerCase())) || []

      if (!shortcut || isTyping(event.target) || event.metaKey || event.ctrlKey || event.altKey) return

      event.preventDefault()
      wake()
      shortcut()
    }

    window.addEventListener('keydown', handleKey)

    return () => window.removeEventListener('keydown', handleKey)
  })

  useEffect(() => {
    if (expanded) wake()
  }, [expanded, playing])

  useEffect(() => () => clearTimeout(idleTimer.current), [])

  useEffect(() => relay?.follow(({bytes, of}) => setReceived({bytes, of: of || file.filesize || 0})), [])

  useEffect(() => {
    const follow = () => setFullScreen(Boolean(frame.current) && document.fullscreenElement === frame.current)

    document.addEventListener('fullscreenchange', follow)

    return () => {
      document.removeEventListener('fullscreenchange', follow)
      // A player closed from full screen would otherwise leave the browser there with nothing in it.
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    }
  }, [])

  const Media = video ? 'video' : 'audio'

  const element = (
    <Media
      ref={media}
      className='player-media'
      src={relay ? relay.source(src) : src}
      preload='metadata'
      playsInline
      onClick={video ? toggle : null}
      onLoadStart={begin}
      onLoadedMetadata={pickUp}
      onError={lose}
      onProgress={followBuffer}
      onTimeUpdate={event => {
        setPosition(event.currentTarget.currentTime)
        followBuffer(event)
      }}
      onSeeking={event => isBuffered(event.currentTarget) || setStalled(true)}
      onSeeked={event => {
        setPosition(event.currentTarget.currentTime)
        setStalled(false)
        followBuffer(event)
      }}
      onWaiting={() => setStalled(true)}
      onCanPlay={() => setStalled(false)}
      onPlaying={() => setStalled(false)}
      onDurationChange={event => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
      onPlay={() => report(true)}
      onPause={() => {
        report(false)
        setStalled(false)
      }}
      onEnded={() => report(false)}
      onVolumeChange={event => setSound({volume: event.currentTarget.volume, muted: event.currentTarget.muted})}
      onRateChange={event => setRate(event.currentTarget.playbackRate)}
    />
  )

  return (
    <div
      ref={frame}
      className={classnames('media-player', {video, filling, expanded, tucked})}
      onPointerMove={expanded ? wake : null}
      onPointerDown={expanded ? wake : null}>
      <div className='player-view'>
        {video ?
          (
            <div
              ref={zoom.ref}
              className={classnames('player-surface', {zoomed: zoom.zoomed, panning: zoom.panning})}
              style={zoom.style}
              {...zoom.handlers}>
              {element}
            </div>
          ) :
          element}

        {video && waiting && <PlayerWait label='Buffering' detail={playing ? STARTS_ON_ITS_OWN : null} />}
        {video && !waiting && (
          <IconButton className={classnames('player-big', {hidden: playing})} aria-label={playing ? 'Pause' : 'Play'} onClick={toggle} tabIndex={playing ? -1 : 0}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
        )}
        {!video && (
          <div className={classnames('player-audio', {waiting})}>
            <span className='player-art'>{waiting ? <WaitRing /> : <AudioIcon />}</span>
            <span className='player-label'>
              <b>{file.filename}</b>
              {waiting ?
                <span role='status'>Buffering{playing && ` · ${STARTS_ON_ITS_OWN}`}</span> :
                <span>{formatBytes(file.filesize)} · {formatClock(duration)}</span>}
            </span>
            <div
              className='player-wave'
              role='slider'
              tabIndex={0}
              aria-label='Seek'
              aria-valuemin={0}
              aria-valuemax={Math.floor(duration)}
              aria-valuenow={Math.floor(position)}
              aria-valuetext={formatClock(position)}
              {...trackHandlers(seekTo)}>
              {waveHeights(file).map((height, index) => {
                const at = (index / WAVE_BARS) * 100

                return <i key={index} className={classnames({played: at < played, buffered: at >= played && at < bufferedTo})} style={{height: `${height}%`}} />
              })}
            </div>
          </div>
        )}
      </div>

      <div className='player-transport' onPointerEnter={() => setOverTransport(true)} onPointerLeave={() => setOverTransport(false)}>
        <div
          className='player-scrub'
          role='slider'
          tabIndex={0}
          aria-label='Seek'
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration)}
          aria-valuenow={Math.floor(position)}
          aria-valuetext={formatClock(position)}
          {...trackHandlers(seekTo)}>
          <div className='scrub-buffered' style={{left: `${bufferedFrom}%`, width: `${bufferedTo - bufferedFrom}%`}} />
          <div className='scrub-played' style={{width: `${played}%`}}><span className='scrub-knob' /></div>
        </div>

        <div className='player-controls'>
          <IconButton className='control' aria-label={`Back ${SKIP_SECONDS} seconds`} onClick={() => skip(-SKIP_SECONDS)}><BackIcon /></IconButton>
          <IconButton className='control play' aria-label={playing ? 'Pause' : 'Play'} onClick={toggle}>
            {waiting && playing ? <span className='control-spinner' /> : <PlayGlyph />}
          </IconButton>
          <IconButton className='control' aria-label={`Forward ${SKIP_SECONDS} seconds`} onClick={() => skip(SKIP_SECONDS)}><ForwardIcon /></IconButton>
          <span className='control-time'>{formatClock(position)} / {formatClock(duration)}</span>
          <span className='control-spacer' />
          <button className='control-rate' type='button' aria-label={`Playback speed ${rate}×`} onClick={nextRate}>{rate}×</button>
          {video && (
            <div className='control-zoom'>
              <IconButton className='control' aria-label='Zoom out' disabled={!zoom.zoomed} onClick={zoom.zoomOut}><ZoomOutIcon /></IconButton>
              <button className='control-level' type='button' title='Fit to frame (0)' onClick={zoom.reset}>{formatZoom(zoom.scale)}</button>
              <IconButton className='control' aria-label='Zoom in' disabled={zoom.largest} onClick={zoom.zoomIn}><ZoomInIcon /></IconButton>
            </div>
          )}
          <div className='control-volume'>
            <IconButton className='control' aria-label={sound.muted ? 'Unmute' : 'Mute'} onClick={toggleMute}>
              {sound.muted ? <MuteIcon /> : <VolumeIcon />}
            </IconButton>
            <div
              className='volume-track'
              role='slider'
              tabIndex={0}
              aria-label='Volume'
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(level * 100)}
              {...trackHandlers(setLevel)}>
              <div className='volume-fill' style={{width: `${level * 100}%`}} />
            </div>
          </div>
          {video && (
            <IconButton className='control' aria-label={expanded ? 'Exit full screen' : 'Full screen'} onClick={toggleFullScreen}>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </div>
      </div>
    </div>
  )
}

MediaPlayer.propTypes = {
  file: PropTypes.shape({
    filename: PropTypes.string.isRequired,
    filesize: PropTypes.number,
    thumbnail: PropTypes.shape({peaks: PropTypes.arrayOf(PropTypes.number)})
  }).isRequired,
  src: PropTypes.string.isRequired,
  video: PropTypes.bool.isRequired,
  filling: PropTypes.bool.isRequired,
  onFill: PropTypes.func.isRequired,
  onNotice: PropTypes.func.isRequired,
  onLost: PropTypes.func.isRequired,
  onPlayingChange: PropTypes.func.isRequired
}

export default MediaPlayer
